import { useState } from 'react';
import { useConfirmationModal, useSnackbars } from '@customerio/pluma-components/react';
import { useRouter } from '@tanstack/react-router';
import type { Tag } from '@customerio/hydra-ui-api';
import { useAnalytics } from '@/services/analytics';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import {
  useDeleteDataIndexAttribute,
  useUpdateDataIndexAttributeMetadata,
  useUpdateDataIndexAttributeTags,
  useUpdateDataIndexEventMetadata,
  useUpdateDataIndexEventTags,
} from '@/services/db/data-index/mutations';
import type { AttributeScope } from '@/services/db/data-index/entity';
import { dataIndexListPath } from '../urls';
import { descriptionError } from '../utils/editing';
import { getLabelForItem } from '../utils/item';
import {
  NOT_SENSITIVE_PRIVACY_LEVEL,
  SENSITIVE_PRIVACY_LEVEL,
  type DataIndexItemKind,
  type DataIndexLocationValue,
} from '../utils/types';
import { DeleteAttributeMessage } from './delete-attribute-message';

/**
 * The writes an item's own page can make: its description and tags, whether
 * it is redacted, and whether it exists at all.
 *
 * Kept apart from the menu so the menu is only a menu — each of these has a
 * different failure to report, and deleting has a confirmation to get
 * through first.
 */

/** The privacy level the backend uses for an attribute someone marked sensitive. */

const SAVE_FAILED_MESSAGE = 'An error occurred when saving the details. Try again.';

export type ItemWriteTarget = {
  workspaceId: number;
  /**
   * What to do once the attribute is gone. The item page has nothing left to
   * show and leaves for the list; the drawer closes and the list hides the
   * row. Defaults to leaving for the list.
   */
  onDeleted?: () => void;
  kind: DataIndexItemKind;
  /** The surface these writes are made from, as analytics names it. */
  location: DataIndexLocationValue;
  scope: AttributeScope;
  name: string;
  /** The description the page shows, which is what a save is compared against. */
  description: string;
  tags: readonly Tag[];
  privacyLevel: number | null;
};

/** What one save can change. An omitted field is one the caller does not edit. */
export type ItemDetailsSave = {
  description: string;
  tagIds: number[];
  /** Only an attribute has one, and only where the plan allows it. */
  isSensitive?: boolean;
};

export type ItemWrites = {
  /**
   * Saves everything the workspace owns about the item; resolves to whether
   * that worked. Refuses a description the form has already rejected, and
   * reports a failed write itself, so a caller only has to decide whether to
   * stay open.
   */
  saveDetails: (values: ItemDetailsSave) => Promise<boolean>;
  toggleSensitivity: () => Promise<void>;
  isChangingSensitivity: boolean;
  /** Asks first, then deletes and leaves for the list. */
  confirmDelete: () => Promise<void>;
};

export function useItemWrites(target: ItemWriteTarget): ItemWrites {
  const { workspaceId, kind, location, scope, name, description, tags, privacyLevel, onDeleted } = target;
  const analytics = useAnalytics();
  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();
  const router = useRouter();
  const navigate = useSafeNavigate();
  const [isChangingSensitivity, setIsChangingSensitivity] = useState(false);

  const analyticsType = getLabelForItem(kind);
  const writeMetadata = useUpdateDataIndexAttributeMetadata({ workspaceId });
  const writeTags = useUpdateDataIndexAttributeTags({ workspaceId });
  const writeEventMetadata = useUpdateDataIndexEventMetadata({ workspaceId });
  const writeEventTags = useUpdateDataIndexEventTags({ workspaceId });
  const remove = useDeleteDataIndexAttribute({ workspaceId });

  const saveDescription = async (next: string) => {
    await (kind === 'event'
      ? writeEventMetadata.mutateAsync({ name, description: next })
      : writeMetadata.mutateAsync({ scope, name, description: next }));
    void analytics.track('dataIndex', 'descriptionUpdated', { type: analyticsType, location });
  };

  const saveTags = async (tagIds: number[]) => {
    await (kind === 'event'
      ? writeEventTags.mutateAsync({ name, tagIds })
      : writeTags.mutateAsync({ scope, name, tagIds }));
    void analytics.track('dataIndex', 'tagsUpdated', { type: analyticsType, location, tag_count: tagIds.length });
  };

  const saveDetails = async (values: ItemDetailsSave) => {
    // The form already shows this under the field, so there is nothing to
    // report and nothing to send.
    if (descriptionError(values.description) != null) {
      return false;
    }

    const current = tags.map((tag) => tag.id).sort((a, b) => a - b);
    const next = [...values.tagIds].sort((a, b) => a - b);
    const nextSensitive = values.isSensitive;

    try {
      if (values.description !== description) {
        await saveDescription(values.description);
      }
      if (current.join() !== next.join()) {
        await saveTags(next);
      }
      if (nextSensitive != null && nextSensitive !== (privacyLevel === SENSITIVE_PRIVACY_LEVEL)) {
        await writeMetadata.mutateAsync({
          scope,
          name,
          privacyLevel: nextSensitive ? SENSITIVE_PRIVACY_LEVEL : NOT_SENSITIVE_PRIVACY_LEVEL,
        });
      }
      return true;
    } catch {
      snackbars.create({ id: 'data-index-item.details-save.error', variant: 'critical', message: SAVE_FAILED_MESSAGE });
      return false;
    }
  };

  const toggleSensitivity = async () => {
    setIsChangingSensitivity(true);
    try {
      await writeMetadata.mutateAsync({
        scope,
        name,
        privacyLevel: privacyLevel === SENSITIVE_PRIVACY_LEVEL ? NOT_SENSITIVE_PRIVACY_LEVEL : SENSITIVE_PRIVACY_LEVEL,
      });
    } catch {
      snackbars.create({
        id: 'attribute.change-sensitivity.error',
        variant: 'critical',
        message: "An error occurred when changing this attribute's sensitivity. Try again.",
      });
    } finally {
      setIsChangingSensitivity(false);
    }
  };

  const confirmDelete = async () => {
    const confirmed = await confirm({
      title: `Delete attribute: ${name}`,
      message: <DeleteAttributeMessage workspaceId={workspaceId} name={name} peoplePlural="people" />,
      confirmLabel: 'Delete attribute',
      isCritical: true,
      // Typing the name is what makes this a decision rather than a click.
      confirmationPhrase: name,
    });
    if (!confirmed) {
      return;
    }

    try {
      await remove.mutateAsync({ scope, name });
    } catch {
      snackbars.create({
        id: 'data-index.attribute.delete.error',
        variant: 'critical',
        message: 'An error occurred while deleting your attribute. Try again later.',
      });
      return;
    }

    snackbars.create({
      id: 'data-index.attribute.delete.success',
      message: 'Your attribute has been scheduled for deletion.',
    });
    // Beside the list there is nowhere to go: the drawer closes and the list
    // it was opened from is already on screen.
    if (onDeleted != null) {
      onDeleted();
      return;
    }

    // Back to the attributes list, which is the only list this can return
    // to: the menu holding Delete renders for a person attribute alone, as
    // it does in the page this replaces.
    const listHref = router.buildLocation({ to: dataIndexListPath(String(workspaceId), 'attributes') }).href;
    navigate(listHref);
  };

  return { saveDetails, toggleSensitivity, isChangingSensitivity, confirmDelete };
}
